import { usePage } from "@inertiajs/react"
import { useCallback } from "react"

import type { TourPage } from "@/components/demo-tour/sections"
import type { DemoTourState } from "@/components/demo-tour/tour-engine"
import TourEngine from "@/components/demo-tour/tour-engine"
import { WELCOME_TOUR_SECTIONS } from "@/components/demo-tour/welcome-sections"
import {
  agencyAccountRootPath,
  agencyDemoTourPath,
  agencyPricingGroupsPath,
} from "@/routes"

interface WelcomeTourPageProps {
  welcome_tour?: {
    demo_tour_state?: DemoTourState | null
    sample_plan_pricing_group_uid?: string
    sample_plan_demo_flow_slug?: string
  } | null
  [key: string]: unknown
}

/** The agency welcome tour (Welcome popup → Prices → Sample Plan → guided
 *  demo hand-off). Unlike the demo-record tours this targets REAL agency
 *  users, once per user per agency; the server only shares `welcome_tour`
 *  when the agency has a seeded Sample Plan demo pricing group. */
export default function AgencyWelcomeTourController() {
  const page = usePage<WelcomeTourPageProps>()
  const welcomeTour = page.props.welcome_tour

  const enabled = Boolean(
    welcomeTour && welcomeTour.demo_tour_state !== undefined,
  )

  const pathForPage = useCallback((target: TourPage): string | null => {
    switch (target.kind) {
      case "agency_console":
        return agencyAccountRootPath()
      case "agency_pricing_groups":
        return agencyPricingGroupsPath()
      default:
        return null
    }
  }, [])

  // The sidebar (and so the Prices link) exists on every console page, so any
  // /agency path satisfies the welcome steps; the pricing page is exact.
  const pageMatches = useCallback((target: TourPage, path: string): boolean => {
    switch (target.kind) {
      case "agency_console":
        return path === "/agency" || path.startsWith("/agency/")
      case "agency_pricing_groups":
        return path === "/agency/pricing_groups"
      default:
        return false
    }
  }, [])

  const isAutoStartPath = useCallback(
    (path: string): boolean =>
      path === "/agency" || path.startsWith("/agency/"),
    [],
  )

  if (!enabled) return null

  return (
    <TourEngine
      sections={WELCOME_TOUR_SECTIONS}
      savedState={welcomeTour?.demo_tour_state}
      persistUrl={agencyDemoTourPath()}
      pathForPage={pathForPage}
      pageMatches={pageMatches}
      isAutoStartPath={isAutoStartPath}
      chrome="none"
    />
  )
}
